/**
 * Pure helpers of the Permit workspace (permits/PermitWorkspace.tsx): the five steps and their status derived from
 * the preview's checks and notes, the checks in two groups (Problems, Passed), next/previous problem, where a check
 * is located, the structural fix-it, units, the photo slots and the generate button. No React; tested in
 * permitWorkspace.test.ts.
 */
import type { MessageKey } from "../i18n/keys";
import { problemsText, type CheckLike, type CheckStatus, type Tr } from "./permits.ts";
import { locateGap } from "./permitEquipment.ts";

export type StepId = "equipment" | "site" | "structure" | "wiring" | "review";
export type WorkStep = Exclude<StepId, "review">;

export const STEPS: { id: StepId; label: MessageKey; short: MessageKey }[] = [
  { id: "equipment", label: "permit.step.equipment", short: "permit.step.equipment" },
  { id: "site", label: "permit.step.site", short: "permit.step.site" },
  { id: "structure", label: "permit.step.structure", short: "permit.step.structure" },
  { id: "wiring", label: "permit.step.wiring", short: "permit.step.wiring" },
  { id: "review", label: "permit.step.review", short: "permit.step.reviewShort" },
];

export function isStep(value: string | null | undefined): value is StepId {
  return STEPS.some((s) => s.id === value);
}

/** The step whose inputs a check is about. Checks are the single source of truth for the steps' status. */
export function stepOfCheck(id: string): WorkStep {
  const gap = locateGap(id);
  if (gap) return gap.step;
  if (id.startsWith("structural_")) return "structure";
  if (id.startsWith("site_") || id === "setbacks_panels" || id === "temperatures" || id === "busbar" || id === "dno" || id === "dnsp") return "site";
  if (id === "rapid_shutdown" || id === "ac_current" || id.startsWith("circuit_") || id.startsWith("wiring_")) return "wiring";
  return "equipment";
}

/** problem: the step has problems; notstarted: optional inputs of the step are left out ("Not started · optional");
 * done; unchecked: the preview has no answer yet (loading) or could not be computed, so nothing is known (never
 * "Done"). Notes that only inform never change a step's state. */
export type StepState = "problem" | "notstarted" | "done" | "unchecked";

export interface StepStatus {
  state: StepState;
  problems: number;
  ok: number;
  /** Notes about optional inputs left out (`missing_input`). */
  missing: number;
  /** Problems that are gaps of a final plan set (`blocking`). */
  gaps: number;
  /** The preview was refused because the design's country is not known (problem `permit_country_required`). */
  needsCountry?: boolean;
}

export interface NoteLike {
  id: string;
  missing_input?: boolean | null;
}

function emptyStatus(): StepStatus {
  return { state: "done", problems: 0, ok: 0, missing: 0, gaps: 0 };
}

function settle(s: StepStatus): StepStatus {
  s.state = s.problems ? "problem" : s.missing ? "notstarted" : "done";
  return s;
}

/** Each step's counts and state from the checks (problems, passed) and the notes; "review" sums every check. */
export function stepStatuses(checks: CheckLike[], notes: NoteLike[] = []): Record<StepId, StepStatus> {
  const out = { equipment: emptyStatus(), site: emptyStatus(), structure: emptyStatus(), wiring: emptyStatus(), review: emptyStatus() };
  for (const c of checks) {
    for (const s of [out[stepOfCheck(c.id)], out.review]) {
      if (c.status === "problem") s.problems += 1;
      else if (c.status === "pass") s.ok += 1;
      if (c.status === "problem" && c.blocking) s.gaps += 1;
    }
  }
  for (const n of notes) if (n.missing_input) out[stepOfCheck(n.id)].missing += 1;
  for (const s of Object.values(out)) settle(s);
  return out;
}

/** The problem code the API answers when the design's country is not known (services/plan_sets.py). */
export const COUNTRY_REQUIRED = "permit_country_required";

/** The steps while the preview has no answer (`error`: its failure, if any): nothing is Done; a missing country is
 * the Equipment step's problem (it is chosen there) and the review's. Round 1 of QA found every step "Done" while
 * the preview was refused for the country. */
export function uncheckedStatuses(error?: unknown): Record<StepId, StepStatus> {
  const needsCountry = (error as { problem?: { code?: string | null } } | null | undefined)?.problem?.code === COUNTRY_REQUIRED;
  const one = (country: boolean): StepStatus =>
    country ? { state: "problem", problems: 1, ok: 0, missing: 0, gaps: 0, needsCountry: true } : { state: "unchecked", problems: 0, ok: 0, missing: 0, gaps: 0 };
  return { equipment: one(needsCountry), site: one(false), structure: one(false), wiring: one(false), review: one(needsCountry) };
}

/** What a step's badge says ("2 problems", "Not started · optional", "Done", "Choose the country"; nothing while
 * unchecked). */
export function stepBadge(t: Tr, s: StepStatus): string {
  if (s.needsCountry) return t("permit.equipmentStep.countryChoose");
  if (s.state === "unchecked") return "";
  if (s.problems && s.gaps === s.problems) return t("permit.stepBadge.missing", { count: s.gaps });
  if (s.problems) return problemsText(t, s.problems);
  if (s.missing) return t("permit.stepBadge.notStarted");
  return t("permit.stepBadge.done");
}

export type CheckGroup = "gap" | "problem" | "pass";

export const GROUPS: { id: CheckGroup; label: MessageKey; hint: MessageKey | null }[] = [
  { id: "gap", label: "permit.group.gap", hint: "permit.group.gapHint" },
  { id: "problem", label: "permit.group.problem", hint: "permit.group.problemHint" },
  { id: "pass", label: "permit.group.pass", hint: null },
];

export function groupOf(status: CheckStatus, blocking?: boolean | null): CheckGroup {
  return status === "problem" ? (blocking ? "gap" : "problem") : "pass";
}

/** The checks in groups (gaps, problems, passed); order within a group is kept. Notes are not checks. */
export function groupChecks<T extends CheckLike>(checks: T[]): { id: CheckGroup; items: T[] }[] {
  const real = checks.filter((c) => c.status !== "info");
  return GROUPS.map((g) => ({ id: g.id, items: real.filter((c) => groupOf(c.status, c.blocking) === g.id) }));
}

/** The checks N / Shift+N step through: the problems, in order. */
export function problemIds(checks: CheckLike[]): string[] {
  return checks.filter((c) => c.status === "problem").map((c) => c.id);
}

/** The next (dir 1) or previous (-1) problem after `current`, wrapping around; the first or last when none is selected. */
export function nextProblem(ids: string[], current: string | null, dir: 1 | -1): string | null {
  if (!ids.length) return null;
  const i = current ? ids.indexOf(current) : -1;
  if (i < 0) return dir === 1 ? ids[0]! : ids[ids.length - 1]!;
  return ids[(i + dir + ids.length) % ids.length]!;
}

/** Where a check points: the step and an anchor in it (a face, a form section, an SLD element). */
export interface LocateTarget {
  step: WorkStep;
  anchor: string;
}

export function faceOfCheck(id: string): string | null {
  const m = /^structural_(.+)_(spacing|rafters)$/.exec(id);
  return m ? m[1]! : null;
}

export function locateCheck(id: string): LocateTarget {
  const gap = locateGap(id);
  if (gap) return gap;
  const face = faceOfCheck(id);
  if (face) return { step: "structure", anchor: `face:${face}` };
  if (id === "structural_rail_table") return { step: "structure", anchor: "rail" };
  if (id.startsWith("structural_")) return { step: "structure", anchor: "loads" };
  if (id === "site_locations") return { step: "site", anchor: "markers" };
  if (id.startsWith("site_ahj")) return { step: "site", anchor: "ahj" };
  if (id === "temperatures") return { step: "site", anchor: "temperatures" };
  if (id === "busbar") return { step: "site", anchor: "service" };
  if (id === "dno" || id === "dnsp") return { step: "site", anchor: "utility" };
  if (id === "rapid_shutdown") return { step: "wiring", anchor: "sld:note.rapid_shutdown" };
  if (id === "ac_current") return { step: "wiring", anchor: "sld:inverter" };
  if (id.startsWith("circuit_")) return { step: "wiring", anchor: "sld" };
  if (/_module$|^module_/.test(id)) return { step: "equipment", anchor: "module" };
  if (/_(inverter|microinverter)$/.test(id)) return { step: "equipment", anchor: "inverter" };
  if (id.startsWith("optimizer") || id.endsWith("_optimizer")) return { step: "equipment", anchor: "optimizer" };
  return { step: "equipment", anchor: "strings" };
}

// ------------------------------------------------------------------------------------------- structural
export interface FaceResult {
  face_id: string;
  label: string;
  status: CheckStatus;
  rafter_spacing_m: number | null;
  allowed_span_m: number | null;
  attachment_spacing_m: number | null;
  notes?: string[];
}

/** The one-click fix of a face's attachment spacing: the largest whole multiple of the rafter spacing within the
 * allowed span (attachments land on rafters), or null when there is none or it is already set. */
export function attachmentFix(face: Pick<FaceResult, "rafter_spacing_m" | "allowed_span_m" | "attachment_spacing_m">): number | null {
  const rafter = face.rafter_spacing_m;
  const allowed = face.allowed_span_m;
  if (!rafter || !allowed) return null;
  const multiple = Math.floor(allowed / rafter + 1e-9);
  if (multiple < 1) return null;
  const fix = Math.round(multiple * rafter * 10000) / 10000;
  if (face.attachment_spacing_m != null && Math.abs(face.attachment_spacing_m - fix) < 0.005) return null;
  return fix;
}

// ----------------------------------------------------------------------------------------------- units
export type Units = "imperial" | "metric";
export type LengthKind = "short" | "long";

/** Short lengths (spacings) in inches or millimetres; long ones (spans) in feet or metres. */
export function lengthUnit(units: Units, kind: LengthKind = "short"): string {
  return units === "imperial" ? (kind === "short" ? "in" : "ft") : kind === "short" ? "mm" : "m";
}

const FACTOR: Record<string, number> = { in: 0.0254, ft: 0.3048, mm: 0.001, m: 1 };

/** Metres → the number shown in the field (rounded sensibly), null stays null. */
export function toDisplay(m: number | null | undefined, units: Units, kind: LengthKind = "short"): number | null {
  if (m == null) return null;
  const v = m / FACTOR[lengthUnit(units, kind)]!;
  const digits = units === "imperial" ? (kind === "short" ? 1 : 2) : kind === "short" ? 0 : 2;
  const k = 10 ** digits;
  return Math.round(v * k) / k;
}

export function fromDisplay(v: number | null | undefined, units: Units, kind: LengthKind = "short"): number | null {
  if (v == null || Number.isNaN(v)) return null;
  return Math.round(v * FACTOR[lengthUnit(units, kind)]! * 100000) / 100000;
}

/** "48 in" / "4'-0"" like the plan set's sheets; "1220 mm" / "1.22 m" in metric (numbers in the reader's locale via
 * `num`, e.g. `(v, d) => intl.formatNumber(v, { minimumFractionDigits: d, maximumFractionDigits: d })`). */
export function formatLength(m: number | null | undefined, units: Units, num: (value: number, digits: number) => string = (v, d) => v.toFixed(d)): string {
  if (m == null) return "—";
  if (units === "imperial") {
    const inches = Math.round(m / 0.0254);
    return inches < 36 ? `${num(inches, 0)} in` : `${Math.floor(inches / 12)}'-${inches % 12}"`;
  }
  return m < 1 ? `${num(Math.round(m * 1000), 0)} mm` : `${num(m, 2)} m`;
}

export function windUnit(units: Units): "mph" | "m_s" {
  return units === "imperial" ? "mph" : "m_s";
}

export function snowUnit(units: Units): "psf" | "kpa" {
  return units === "imperial" ? "psf" : "kpa";
}

// ---------------------------------------------------------------------------------------------- photos
export type PhotoSlot = "main_panel_open" | "main_panel_label" | "meter" | "attic" | "roof_overview" | "sub_panel" | "inverter_location" | "roof_detail" | "other";

const slot = (s: PhotoSlot, key: string) => ({ slot: s, title: `permit.photoSlot.${key}.title` as MessageKey, why: `permit.photoSlot.${key}.why` as MessageKey });
export const PHOTO_SLOTS: { slot: PhotoSlot; title: MessageKey; why: MessageKey }[] = [
  slot("main_panel_open", "mainPanelOpen"),
  slot("main_panel_label", "mainPanelLabel"),
  slot("meter", "meter"),
  slot("attic", "attic"),
  slot("roof_overview", "roofOverview"),
  slot("sub_panel", "subPanel"),
  slot("inverter_location", "inverterLocation"),
  slot("roof_detail", "roofDetail"),
  slot("other", "other"),
];

/** Progress over the suggested photos ("2 of 5 photos"); photos are optional. */
export function photoProgress(t: Tr, suggested: string[], missing: string[]): { done: number; total: number; label: string } {
  const total = suggested.length;
  const done = suggested.filter((s) => !missing.includes(s)).length;
  return { done, total, label: t("permit.photos.progress", { done, total }) };
}

// -------------------------------------------------------------------------------------------- generate
export interface GenerateState {
  label: string;
  disabled: boolean;
  /** Why it is off (shown under it), or null. */
  reason: string | null;
}

/** "5 Lux", "0.50 Lux" from hundredths. */
export function luxText(t: Tr, hundredths: number): string {
  return t("permit.lux", { lux: hundredths / 100 });
}

/** The generate card's main button: the cost on the button (Runway), or why it is off. Problems never block it. */
export function generateState(t: Tr, o: { enabled: boolean; canEdit: boolean; cost: number; hasHistory: boolean; dirty?: boolean; ready: boolean; gaps?: number }): GenerateState {
  const lux = o.cost / 100;
  const label = o.gaps
    ? o.cost > 0 ? t("permit.generate.openItemsCost", { lux }) : t("permit.generate.openItemsFree")
    : o.cost > 0 ? t("permit.generate.planSetCost", { lux }) : o.hasHistory ? t("permit.generate.regenerateFree") : t("permit.generate.planSetFree");
  if (!o.enabled) return { label, disabled: true, reason: t("permit.generate.off") };
  if (!o.canEdit) return { label, disabled: true, reason: t("permit.generate.viewOnly") };
  if (o.dirty) return { label, disabled: true, reason: t("permit.generate.saveFirst") };
  if (!o.ready) return { label, disabled: true, reason: t("permit.generate.waiting") };
  return { label, disabled: false, reason: null };
}

// ---------------------------------------------------------------------------------------------- history
export type Summary = Record<CheckStatus, number>;

/** What changed between a plan set's checks and the previous one's ("problem −1", "pass +2"). */
export function checksDiff(now: Partial<Summary>, before: Partial<Summary>): { status: CheckStatus; delta: number }[] {
  return (["problem", "pass"] as CheckStatus[])
    .map((status) => ({ status, delta: (now[status] ?? 0) - (before[status] ?? 0) }))
    .filter((d) => d.delta !== 0);
}

/** The plan-set sheet a check is printed on (thumbnail badges): structural on PV-3, site details on PV-1, the
 * diagram's on E-2, the rest on the electrical calculations E-1. */
export function sheetOfCheck(id: string): string {
  if (id.startsWith("gate_")) return "PV-0"; // gaps: listed on the cover
  if (id.startsWith("structural_")) return "PV-3";
  if (id.startsWith("site_")) return "PV-1";
  if (id === "rapid_shutdown") return "E-2";
  return "E-1";
}

export interface CheckRow {
  id: string;
  title: string;
  status: string;
  detail: string;
}

export type CheckChange =
  | { kind: "added" | "removed"; title: string; status: string }
  | { kind: "status"; title: string; from: string; to: string }
  | { kind: "detail"; title: string; from: string; to: string };

/** What changed check by check between two plan sets (the compare row of the history): checks that appeared or
 * went away, changed status, or say something else (e.g. fewer photos missing). */
export function checkChanges(now: CheckRow[], before: CheckRow[]): CheckChange[] {
  const old = new Map(before.map((c) => [c.id, c]));
  const out: CheckChange[] = [];
  for (const c of now) {
    const b = old.get(c.id);
    if (!b) out.push({ kind: "added", title: c.title, status: c.status });
    else if (b.status !== c.status) out.push({ kind: "status", title: c.title, from: b.status, to: c.status });
    else if (b.detail !== c.detail) out.push({ kind: "detail", title: c.title, from: b.detail, to: c.detail });
    old.delete(c.id);
  }
  for (const b of old.values()) out.push({ kind: "removed", title: b.title, status: b.status });
  return out;
}
